import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Inject, Injector, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { of, Subject } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { BackboneAdapterService } from '../../../shared/backbone-adapter.service';
import { OwlCarouselOptions } from '../../../shared/carousel/owl-carousel-options';
import { GTMService } from '../../../shared/gtm.service';
import { ModalService } from '../../../shared/modal.service';
import { OneSignalService } from '../../../shared/one-signal.service';
import { MODAL_DATA } from '../../../shared/tokens/modal-data.token';
import { AnalyticsOutcomeEvents } from '../../../shared/types/analytics-outcome.types';
import { CohortTrackEvents } from '../../../shared/types/cohort-track-events.enum';
import { LiveClassDto } from '../../../shared/types/LiveClassDto';
import { OneSignalTags } from '../../../shared/types/one-signal-tags';
import { Program } from '../../../shared/types/program-type';
import { OeAppPersonService } from '../../../shared/oe-app-person.service';
import { GenericModalService } from '../../../shared/generic-modal/generic-modal.service';
import { TranslationService } from '../../../shared/translation.service';
import { COHORT_CONFIG_TYPE } from '../cohort.config';
import { COHORT_CONFIG_TOKEN } from '../cohort.config.token';
import { CohortResetModalComponent, CohortResetModalData } from '../cohort-reset-modal/cohort-reset-modal.component';
import { CohortResetService } from '../cohort-reset.service';
import { CohortService } from '../cohort.service';
import { WindowService } from '../../../shared/window.service';
import { NotificationService } from '../../../shared/notification.service';

@Component({
  selector: 'lp2-cohort-carousel',
  templateUrl: './cohort-carousel.component.html',
  styleUrls: ['./cohort-carousel.component.scss', '../../../shared/carousel/carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class CohortCarouselComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();


  public readonly carouselSettings: OwlCarouselOptions = {
    nav: true,
    margin: 12,
    loop: false,
    rewind: false,
    autoWidth: true,
    itemClass: 'cohort-carousel-item',
    dotsClass: 'cohort-carousel-dots',
    responsive: {
      0: {
        items: 2,
        slideBy: 2,
      },
      768: {
        items: 3,
        slideBy: 3,
      },
      992: {
        items: 3,
        slideBy: 3,
      },
    },
  };

  public hasError = false;
  public isCheckingEligibility = false;

  @HostBinding('class.error-state')
  public get isErrorState() {
    return this.hasError;
  }

  /** Undefined when the program payload omits the flag — the template treats that as disabled. */
  public get changeCohortEnabled(): Program['changeCohortEnabled'] {
    return this.oeAppPersonService.changeCohortEnabled;
  }

  public readonly liveClassesData$ = this.cohortService.liveClassesPoll$.pipe(
    tap((classes) => {
      this.liveClasses = classes;
    }),
    catchError((error) => {
      this.hasError = true;
      return of(error);
    })
  );

  private liveClasses: LiveClassDto[] = [];

  constructor(
    @Inject(COHORT_CONFIG_TOKEN) public cohortConfig: COHORT_CONFIG_TYPE,
    private cohortService: CohortService,
    private cohortResetService: CohortResetService,
    private modalService: ModalService,
    private genericModalService: GenericModalService,
    private translationService: TranslationService,
    private windowService: WindowService,
    private notificationService: NotificationService,
    private oneSignalService: OneSignalService,
    private gtmService: GTMService,
    private backbone: BackboneAdapterService,
    private oeAppPersonService: OeAppPersonService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.listenBackboneToLiveClassJoin();
  }

  public onChangeCohortClick() {
    this.gtmService.sendGA4Event(CohortTrackEvents.COHORT_CHANGE_INITIATED);
    this.isCheckingEligibility = true;
    this.cdr.markForCheck();

    this.cohortResetService
      .getResetEligibility$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (eligibility) => {
          this.isCheckingEligibility = false;
          this.cdr.markForCheck();
          this.openResetModal({ eligibility });
        },
        () => {
          this.isCheckingEligibility = false;
          this.cdr.markForCheck();
          this.showGenericError();
        }
      );
  }

  public onJoinClicked(liveClass: LiveClassDto) {
    this.notificationService
      .setDeleteFlag(liveClass.reminderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.windowService.openUrl(liveClass.classroomUrl);
        this.trackCohortClassAttended(liveClass);
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private openResetModal(data: CohortResetModalData) {
    this.modalService.open(CohortResetModalComponent, {
      windowClass: 'cohort-reset-modal-window',
      centered: true,
      backdrop: 'static',
      keyboard: false,
      injector: Injector.create({
        providers: [{ provide: MODAL_DATA, useValue: data }],
      }),
    });
  }

  private showGenericError() {
    this.genericModalService.show(
      this.translationService.translate('alert.titleError'),
      this.translationService.translate('errors.unablePerformAction')
    );
  }

  private listenBackboneToLiveClassJoin() {
    this.backbone.addListener('liveclass:join', (liveClassScheduleId: number) => {
      const liveClass = this.liveClasses.find((c) => c.id === liveClassScheduleId);
      if (liveClass) {
        this.trackCohortClassAttended(liveClass);
      }
    });
  }

  private trackCohortClassAttended(liveClass: LiveClassDto) {
    this.oneSignalService.trackOutcome(AnalyticsOutcomeEvents.LAST_COHORT_CLASS_ATTENDED);
    this.oneSignalService.setTag(OneSignalTags.LAST_COHORT_CLASS_TAKEN_TIMESTAMP, Date.now().toString());
    this.oneSignalService.setTag(OneSignalTags.LAST_COHORT_CLASS_TAKEN_TYPE, liveClass.subType);
    this.oneSignalService.setTag(OneSignalTags.LAST_COHORT_CLASS_TAKEN_NAME, liveClass.title);
  }
}



// WEBPACK FOOTER //
// ./ts/app/home/cohort/cohort-carousel/cohort-carousel.component.ts